import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { weeklyPlansApi } from "@/features/tasks/api/weeklyPlans";
import type { WeeklyPlanFormData } from "@/features/tasks/types";

const planKeys = {
  all: ["weekly-plans"] as const,
  list: (params?: object) => [...planKeys.all, "list", params] as const,
  detail: (id: string) => [...planKeys.all, "detail", id] as const,
  current: () => [...planKeys.all, "current"] as const,
  carryOver: () => [...planKeys.all, "carry-over"] as const,
};

export function useWeeklyPlans(params?: { project?: string }) {
  return useQuery({
    queryKey: planKeys.list(params),
    queryFn: () => weeklyPlansApi.list(params),
  });
}

export function useCurrentWeekPlans() {
  return useQuery({
    queryKey: planKeys.current(),
    queryFn: () => weeklyPlansApi.currentWeek(),
  });
}

export function useCarryOver() {
  return useQuery({
    queryKey: planKeys.carryOver(),
    queryFn: () => weeklyPlansApi.carryOver(),
  });
}

export function useCreateWeeklyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: WeeklyPlanFormData) => weeklyPlansApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: planKeys.all });
    },
  });
}

export function useUpdateWeeklyPlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<WeeklyPlanFormData>) => weeklyPlansApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: planKeys.all });
    },
  });
}

export function useDeleteWeeklyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => weeklyPlansApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: planKeys.all });
    },
  });
}
